// Types
import type { TFeatureOpportunityStatsConfig } from '~/shared/stats/types';
import type { EFeatureName } from '~/misc/feature/types';

// Config
import { FEATURE_OPPORTUNITY_STATS_CONFIG } from '~/shared/stats/config';

export const useFeatureOpportunityStats = <
  InputStatsType extends { category: string; count: number }
>() => {
  const mapFeatureOpportunityStats = <MappedType>(
    stats: InputStatsType[],
    mapCallback: (
      featuresStats: InputStatsType[],
      config: TFeatureOpportunityStatsConfig
    ) => MappedType,
    protectedFeatures: EFeatureName[] | null = null
  ): MappedType[] => {
    return featureOpportunityStatsConfig(protectedFeatures).map(
      (config: TFeatureOpportunityStatsConfig) => mapCallback(featureStats(stats, config), config)
    );
  };

  const featureOpportunityStatsConfig = (
    protectedFeatures: EFeatureName[] | null = null
  ): TFeatureOpportunityStatsConfig[] => {
    let config: TFeatureOpportunityStatsConfig[] = FEATURE_OPPORTUNITY_STATS_CONFIG;

    if (protectedFeatures) {
      config = config.filter((config: TFeatureOpportunityStatsConfig) =>
        config.protectedFeature ? protectedFeatures.includes(config.protectedFeature) : true
      );
    }

    return config;
  };

  const featureStats = (
    stats: InputStatsType[],
    config: TFeatureOpportunityStatsConfig
  ): InputStatsType[] =>
    stats.filter((item: InputStatsType) => config.lookupCategories.includes(item.category));

  const featureOpportunityStatsTableData = (
    stats: InputStatsType[],
    protectedFeatures: EFeatureName[] | null = null
  ): number[] => {
    let total: number = 0;

    const data: number[] = mapFeatureOpportunityStats<number>(
      stats,
      (featuresStats: InputStatsType[]): number => {
        const count: number = featuresStats.reduce(
          (acc: number, cur: InputStatsType) => acc + Number(cur.count),
          0
        );

        total += count;

        return count;
      },
      protectedFeatures
    );

    data.unshift(total);

    return data;
  };

  const featureOpportunityStatTitles = (
    protectedFeatures: EFeatureName[] | null = null
  ): string[] =>
    featureOpportunityStatsConfig(protectedFeatures).map(
      (item: TFeatureOpportunityStatsConfig) => item.title
    );

  return {
    mapFeatureOpportunityStats,
    featureOpportunityStatsTableData,
    featureOpportunityStatTitles,
  };
};
